import { History, MoreVertical, Plus, X } from 'lucide-react';
import { useMemo } from 'react';

import { AvartarType } from '@/components/Avatar/avatarConst';
import ProfileAvatar from '@/components/Avatar/ProfileAvatar';
import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import ToggleRadio from '@/components/Common/ToggleRadio';
import Empty from '@/components/Empty/Empty';
import List from '@/components/List/List';
import Menu from '@/components/Menu/Menu';
import { ScrollArea } from '@/components/shadcn/ui/scroll-area';
import Typo from '@/components/Typography/Typo';
import {
  deleteNotification,
  enableNotification,
  useNotificationListQuery,
} from '@/entities/notification';
import type { AlertModalState } from '@/features/(signal)/signals';
import { cn } from '@/lib/cn';
import { popup } from '@/stores/pure/PopupStore';
import { toast } from '@/stores/pure/ToastStore';
import { WORKFLOW_TYPE } from '@/types/workflowConst';

interface SignalAlertListPaneProps {
  onClose: () => void;
  alertModalState: AlertModalState;
}

export const SignalAlertListPane = ({
  onClose,
  alertModalState,
}: SignalAlertListPaneProps) => {
  const alertTypes = useMemo(() => [WORKFLOW_TYPE.SIGNAL_ALERT], []);
  const { data, refetch } = useNotificationListQuery(alertTypes, {
    staleTime: 0,
  });
  const alerts = useMemo(
    () =>
      data?.filter(
        (n): n is SignalAlertWorkflow =>
          n.type === WORKFLOW_TYPE.SIGNAL_ALERT,
      ),
    [data],
  );

  const onEdit = (alertId: string) => {
    const alert = alerts?.find((alert) => alert.alias === alertId);
    if (!alert) return;
    alertModalState.openEdit(alert);
  };

  const onDelete = (alertId: string) => {
    popup({
      type: 'confirm',
      popupTheme: 'negative',
      title: 'Delete Alert',
      message: 'Are you sure you want to delete this alert?',
      onConfirm: async () => {
        await deleteNotification({ notification_id: alertId });
        refetch();
        toast('Alert deleted successfully', 'success');
      },
    });
  };

  const onToggle = async (alertId: string, status: boolean) => {
    await enableNotification({ notification_id: alertId, enable: status });
    toast(status ? 'Alert enabled' : 'Alert disabled', 'success');
    refetch();
  };

  return (
    <VFlex className="w-full h-screen bg-white overflow-auto">
      {/* Header */}
      <Flex a="center" j="space-between" className="px-6 py-4">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold text-gray-900">Alerts</h2>
        </div>
        <div className="flex items-center gap-2">
          <IconButton
            icon={<History className="size-4" />}
            sizeType="m"
            onClick={() => {
              const alertIds = alerts?.map((alert) => alert.alias) ?? [];
              alertModalState.openHistory(alertIds);
            }}
            disabled={!alerts || alerts.length === 0}
          />
          <Button
            variant="primary"
            sizeType="m"
            onClick={() => alertModalState.openCreate()}
            leftIcon={<Plus className="size-4" />}
          >
            New
          </Button>
          <IconButton
            icon={<X className="size-4" />}
            sizeType="m"
            onClick={onClose}
          />
        </div>
      </Flex>

      {/* Alert List */}
      <ScrollArea className="flex-1 overflow-auto size-full">
        <List
          className="h-full"
          data={alerts}
          placeholder={{
            node: (
              <Empty
                title="No alerts"
                description="Create an alert to get started."
              />
            ),
          }}
          render={(alert: SignalAlertWorkflow) => (
            <AlertItem
              key={alert.alias}
              alert={alert}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggle={onToggle}
            />
          )}
        />
      </ScrollArea>
    </VFlex>
  );
};

const AlertItem = ({
  alert,
  onEdit,
  onDelete,
  onToggle,
}: {
  alert: SignalAlertWorkflow;
  onEdit: (alertId: string) => void;
  onDelete: (alertId: string) => void;
  onToggle: (alertId: string, status: boolean) => void;
}) => {
  const getChannelName = (channel: 'SLACK' | 'MAIL') => {
    return channel === 'MAIL' ? 'Email' : 'Slack';
  };

  return (
    <div
      key={alert.alias}
      className="border-b-1 border-gray-200 last:border-b-0"
    >
      <div
        className={cn(
          'px-6 py-4 cursor-pointer transition-colors hover:bg-gray-50 flex items-center justify-between gap-4',
        )}
      >
        <ToggleRadio
          status={alert.enable}
          onChange={(status) => onToggle(alert.alias, status)}
        />
        <Flex a="center" g={12} className="flex-1 min-w-0">
          {/* Alert Details */}
          <VFlex g={4} className="flex-1 min-w-0">
            <Typo type="label2" weight="sb" className="text-gray-900">
              {alert.title}
            </Typo>
            <Flex a="center" g={6}>
              <span className="text-xs text-gray-900">Created by</span>
              <ProfileAvatar
                withText
                withAvatar={false}
                text={alert.updated_by}
                type={AvartarType.MEMBER}
              />
              <span className="text-xs text-gray-600">Via</span>
              <span className="text-xs text-gray-600">
                {getChannelName(alert.channel.type)}
              </span>
            </Flex>
          </VFlex>
        </Flex>

        {/* Kebab Menu */}
        <Menu
          triggerNode={
            <IconButton
              icon={<MoreVertical className="size-4" />}
              sizeType="m"
              outlined={false}
            />
          }
          options={[
            {
              label: 'Edit',
              onClick: () => onEdit?.(alert.alias),
            },
            {
              label: 'Delete',
              onClick: () => onDelete?.(alert.alias),
            },
          ]}
        />
      </div>
    </div>
  );
};
